import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  HStack,
  Text,
  Textarea,
  VStack,
  Image,
  FormControl,
  Flex,
  Spinner,
  useToast
} from '@chakra-ui/react';
import CloseModalButton from '../../../../../../assets/close-modal-icon.svg';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { commentApi } from '../../../../../../apis/comment.api';
import ToastBtnMessage from '../../../../../ToastBtnMessage';
import { ModerationType, ResourceType } from '../../../../../../types/moderation.types';
import { moderationApi } from '../../../../../../apis/moderation.api';
import { formatViolationType } from '../../../../../../utils/helper.util';
import { DataLayer } from '../../../../../../utils/data-layer';
import { UnauthorizedDispatcher } from 'src/utils/unauthorized-dispatcher';

const ReportModal = ({ setOpen, commentId, targetUserId, username }) => {
  const [note, setNote] = useState('');
  const [selectedViolations, setSelectedViolations] = useState<ModerationType[]>([]);
  const [isIgnoreWriter, setIsIgnoreWriter] = useState(false);
  const textareaRef = useRef(null);
  const toast = useToast();
  const queryClient = useQueryClient();
  const userId = JSON.parse(localStorage.getItem('user'))?.id;

  const maxLength = 2000;

  const { data: violationTypes = [], isFetching } = useQuery({
    queryKey: ['violationTypes'],
    queryFn: moderationApi.getViolationTypes,
    staleTime: 10000
  });

  const { mutateAsync: submitReport, isPending: isReportPending } = useMutation({
    mutationFn: moderationApi.createReport,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['reports']
      });
    }
  });
  const { mutateAsync: blockUser } = useMutation({
    mutationFn: commentApi.blockUser,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['block']
      });
    }
  });

  const handleViolationToggle = (violation: ModerationType) => {
    setSelectedViolations((current) =>
      current.includes(violation) ? current.filter((v) => v !== violation) : [...current, violation]
    );
  };

  const handleReport = async () => {
    try {
      if (!userId) return UnauthorizedDispatcher('required');
      await submitReport({
        targetId: commentId,
        targetType: ResourceType.COMMENT,
        violations: selectedViolations,
        detail: note
      });
      if (isIgnoreWriter) {
        // Track writer ignore event
        if (username) {
          DataLayer.trackWriterIgnore(username);
        }
        await blockUser(targetUserId);
      }
      toast({
        render: () => (
          <ToastBtnMessage
            title={
              isIgnoreWriter
                ? 'Comment reported, you won’t see it again.'
                : 'Successfully reported the comment'
            }
            isError={false}
            color="jet-black"
            isHiddenButton
          />
        ),
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top'
      });
      setOpen(false);
    } catch (error) {
      console.error('Failed to report comment:', error);
      toast({
        render: () => (
          <ToastBtnMessage
            title={error?.response?.data?.message}
            isError={true}
            color="red"
            isHiddenButton
          />
        ),
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top'
      });
    }
  };

  const handleChangeNote = (e) => {
    adjustHeight();
    setNote(e.target.value);
  };

  const adjustHeight = () => {
    textareaRef.current.style.height = '';
    const newHeight = Math.min(textareaRef.current.scrollHeight + 1, 150); // 32 55 78
    textareaRef.current.style.height = `${newHeight}px`;
  };

  useEffect(() => {
    adjustHeight();
  });

  return (
    <VStack
      p={6}
      gap={6}
      alignItems={'start'}
      w="100%"
      h="100%"
      position={'relative'}
      bg={'white'}
      overflow={'auto'}
    >
      <HStack
        justifyContent="space-between"
        alignItems={'center'}
        pb={2}
        borderBottom={'1px solid #D1C8C4'}
        w={'100%'}
        bg={'white'}
      >
        <Text fontSize="20px" fontWeight={800}>
          Report Comment
        </Text>
        <Image
          src={CloseModalButton}
          cursor={'pointer'}
          _hover={{ opacity: '0.8' }}
          onClick={() => setOpen(false)}
        />
      </HStack>
      <Box
        w="100%"
        display={'flex'}
        flexDir={'column'}
        gap={6}
        alignItems={'start'}
        overflowY={'auto'}
        pr={4}
      >
        <Box w={'100%'}>
          <Text mb={4}>
            Please select the issue(s) that apply. You can choose one or more violations.
          </Text>

          <VStack spacing={4} align="start">
            {!isFetching ? (
              violationTypes.map((violationType) => {
                const isSelected = selectedViolations.includes(violationType.type);
                return (
                  <Box key={violationType.type}>
                    <Checkbox
                      isChecked={isSelected}
                      onChange={() => handleViolationToggle(violationType.type)}
                    >
                      <Text fontWeight={isSelected ? '800' : '600'}>
                        {formatViolationType(violationType.type)}
                      </Text>
                    </Checkbox>
                    <Text fontWeight={400} pl={'28px'}>
                      {violationType.description}
                    </Text>
                  </Box>
                );
              })
            ) : (
              <Spinner my={'24px'} alignSelf={'center'} size={'md'} color="green.500" />
            )}
          </VStack>
        </Box>

        <Box w={'100%'}>
          <Text mb={2} fontWeight={600}>
            Notes
          </Text>
          <FormControl>
            <Textarea
              value={note}
              ref={textareaRef}
              onChange={handleChangeNote}
              placeholder="Write any additional notes here"
              p={{ base: '12px 12px', '2xl': '17px 24px' }}
              fontSize={{ base: 'sm', '2xl': 'md' }}
              rounded={'0.75rem'}
              minHeight={'unset'}
              resize={'none'}
              borderWidth={'1px'}
              borderColor={'light-gray'}
              color={'jet-black'}
              bg={'#FAFAFA'}
              lineHeight={'1.25rem'}
              _hover={{
                borderColor: 'inherit'
              }}
              _focus={{
                boxShadow: 'none'
              }}
              _focusVisible={{
                borderColor: 'twygs-green',
                borderWidth: '1.5px'
              }}
              css={{
                '&::-webkit-scrollbar': {
                  display: 'none'
                }
              }}
              maxLength={maxLength}
              mb={2}
            />
            <Flex justify="end" fontSize="sm" color="gray.500">
              <Text fontSize={'sm'} color={'dim-gray'}>
                {note.length}/{maxLength.toLocaleString()}
              </Text>
            </Flex>
          </FormControl>
        </Box>
        <Box w={'100%'}>
          <Text mb={4}>Do you want to ignore this writer?</Text>

          <VStack spacing={3} align="start" mb={6}>
            <Box>
              <Checkbox onChange={() => setIsIgnoreWriter(!isIgnoreWriter)}>
                <Text fontWeight={isIgnoreWriter ? '800' : '600'}>
                  Ignore the writer of this comment
                </Text>
              </Checkbox>
              <Text fontWeight={400} pl={'28px'}>
                Don't show content from this writer again.
              </Text>
            </Box>
          </VStack>
        </Box>
      </Box>

      <HStack w={'100%'} justifyContent={'end'}>
        <Button
          bg={'white'}
          border={'2px solid'}
          borderColor={'growth-green'}
          rounded={'8px'}
          px={'18px'}
          w={'fit-content'}
          onClick={() => setOpen(false)}
          h={'42px'}
          color={'growth-green'}
          fontWeight={900}
          fontSize={'md'}
          _hover={{ opacity: '0.7' }}
        >
          Cancel
        </Button>
        <Button
          bg={'growth-green'}
          color={'white'}
          rounded={'8px'}
          w={'fit-content'}
          h={'42px'}
          px={'18px'}
          onClick={handleReport}
          fontWeight={900}
          fontSize={'md'}
          _hover={{ opacity: '0.7' }}
          _disabled={{
            opacity: '0.4',
            cursor: 'not-allowed',
            bg: 'white',
            color: 'dim-gray',
            borderColor: 'light-gray'
          }}
          isLoading={isReportPending}
          isDisabled={selectedViolations.length === 0}
        >
          Report
        </Button>
      </HStack>
    </VStack>
  );
};

export default ReportModal;
